import type { ToolsetId } from "@ttc-spectree/toolbox-contract";

/**
 * Which SpecTree Toolbox tool a path belongs to (epic PI-SPEC-SW-E282).
 * The Toolbox chrome (tool switcher, icon rail, title bar) adapts to it:
 * - `grooming`: `/grooming` and below;
 * - `formulator`: `/toolbox/formulator` and below;
 * - `fogbugz`: `/toolbox/fogbugz` and below (the FogBugz event log summary,
 *   epic PI-SPEC-SW-E307);
 * - `fogbugz-formulator`: `/toolbox/fogbugz-formulator` and below (the
 *   Toolbox FogBugz Formulator, epic PI-SPEC-SW-E309);
 * - `ask`: `/toolbox/ask` and below (Product Q&A, epic PI-SPEC-SW-E310);
 * - `fogbugz-to-ado`: `/toolbox/fogbugz-to-ado` and below (bring a FogBugz
 *   case to the ADO board as a Bug, epic PI-SPEC-SW-E311);
 * - `home`: everything else (`/toolbox`, `/toolbox/setup`).
 */
export type ToolboxTool =
  | "grooming"
  | "formulator"
  | "fogbugz"
  | "fogbugz-formulator"
  | "ask"
  | "fogbugz-to-ado"
  | "home";

/** Toolbox home: the Toolbox's start page (the rail's Home item, UX-PM-45). */
export const TOOLBOX_HOME_PATH = "/toolbox";

/** The Formulator's page in SpecTree Toolbox. */
export const FORMULATOR_PATH = "/toolbox/formulator";

/**
 * The FogBugz event log summary page in SpecTree Toolbox (epic
 * PI-SPEC-SW-E307, ER-1663): summarize an `.evtx` on an existing FogBugz
 * case with the person's own Claude, review it, then post it to the case.
 */
export const FOGBUGZ_PATH = "/toolbox/fogbugz";

/** The FogBugz tool's plain name on home, the switcher, the palette and the page title. */
export const FOGBUGZ_TOOL_NAME = "FogBugz event log summary";

/**
 * The FogBugz Formulator page in SpecTree Toolbox (epic PI-SPEC-SW-E309,
 * ER-1665 Package C): write a FogBugz case while the person's own Claude
 * reads the product code. Not under {@link FOGBUGZ_PATH}: its own tool.
 */
export const FOGBUGZ_FORMULATOR_PATH = "/toolbox/fogbugz-formulator";

/** The FogBugz Formulator's plain name on home, the switcher, the rail, the palette and the page title (Resolved Decision 1). */
export const FOGBUGZ_FORMULATOR_TOOL_NAME = "FogBugz Formulator";

/**
 * The Product Q&A page in SpecTree Toolbox (epic PI-SPEC-SW-E310, ER-1667
 * Package F): ask how the product behaves; the person's own Claude reads the
 * product's code and answers in plain words, saying whether a written spec
 * backs it.
 */
export const ASK_PATH = "/toolbox/ask";

/** Product Q&A's plain name on home, the switcher, the rail, the palette and the page title. */
export const ASK_TOOL_NAME = "Product Q&A";

/**
 * The FogBugz to ADO page in SpecTree Toolbox (epic PI-SPEC-SW-E311, ER-1666
 * Package E): a Project Manager brings an existing FogBugz case to the ADO
 * board as a complete Bug. Not under {@link FOGBUGZ_PATH}: its own tool.
 */
export const FOGBUGZ_TO_ADO_PATH = "/toolbox/fogbugz-to-ado";

/** The FogBugz to ADO tool's plain name on home, the switcher, the rail and the page title. Never "Bug Bridge". */
export const FOGBUGZ_TO_ADO_TOOL_NAME = "FogBugz to ADO";

/** The FogBugz to ADO tool's one-line description on the home tile. */
export const FOGBUGZ_TO_ADO_TOOL_DESCRIPTION = "Bring a FogBugz case to the ADO board as a complete Bug";

/**
 * Whether FogBugz to ADO shows on home, the switcher and the rail (epic
 * PI-SPEC-SW-E311, task PI-SPEC-SW-1640-1): the `fogbugz-to-ado` capability
 * on some project (Admins and Project Managers), the person's own saved
 * FogBugz key (the case is read as them) and, when `bridge` is given, an app
 * whose bridge exposes the toolset. Without a key the tool is hidden, not
 * shown with a Settings chip; its route still says "Save your FogBugz key".
 * Pure; exported for tests.
 */
export function fogbugzToAdoVisible(
  mayBringCases: boolean,
  hasFogbugzKey: boolean,
  bridge?: { app: { contracts: Partial<Record<ToolsetId, number>> } } | null,
): boolean {
  if (!mayBringCases || !hasFogbugzKey) return false;
  if (bridge === undefined) return true;
  return bridge?.app.contracts["fogbugz-to-ado"] !== undefined;
}

/** The Toolbox role screen (epic PI-SPEC-SW-E283, feature PI-SPEC-SW-1431). */
export const ROLES_PATH = "/toolbox/roles";

/** The role screen, opened on `project` when given. */
export function rolesPath(project?: string | null): string {
  return project ? `${ROLES_PATH}?${new URLSearchParams({ project }).toString()}` : ROLES_PATH;
}

/** True on `/toolbox/formulator` and its sub-pages. */
export function isFormulatorPath(pathname: string): boolean {
  return /^\/toolbox\/formulator(\/|$)/.test(pathname);
}

/** True on `/toolbox/fogbugz` and its sub-pages. */
export function isFogbugzPath(pathname: string): boolean {
  return /^\/toolbox\/fogbugz(\/|$)/.test(pathname);
}

/**
 * True on `/toolbox/fogbugz-formulator` and its sub-pages. `isFogbugzPath`
 * never matches these (its pattern needs `/` or the end after `fogbugz`).
 */
export function isFogbugzFormulatorPath(pathname: string): boolean {
  return /^\/toolbox\/fogbugz-formulator(\/|$)/.test(pathname);
}

/**
 * True on `/toolbox/fogbugz-to-ado` and its sub-pages. `isFogbugzPath` never
 * matches these (its pattern needs `/` or the end after `fogbugz`).
 */
export function isFogbugzToAdoPath(pathname: string): boolean {
  return /^\/toolbox\/fogbugz-to-ado(\/|$)/.test(pathname);
}

/** True on `/toolbox/ask` and its sub-pages (never `/toolbox/asked`). */
export function isAskPath(pathname: string): boolean {
  return /^\/toolbox\/ask(\/|$)/.test(pathname);
}

/** The tool a path belongs to. Pure; exported for tests. */
export function toolFromPath(pathname: string): ToolboxTool {
  if (/^\/grooming(\/|$)/.test(pathname)) return "grooming";
  if (isFormulatorPath(pathname)) return "formulator";
  // Before the event log summary, so the Formulator is never read as `fogbugz`.
  if (isFogbugzFormulatorPath(pathname)) return "fogbugz-formulator";
  if (isFogbugzToAdoPath(pathname)) return "fogbugz-to-ado";
  if (isFogbugzPath(pathname)) return "fogbugz";
  if (isAskPath(pathname)) return "ask";
  return "home";
}
